import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "@tanstack/react-router";
import type { Tool } from "@ag-ui/client";
import { useAgentCapabilities, useWhoDrugWorkspace } from "../../shared/context/AgentCapabilityContext";
import { useUiContext } from "../../shared/context/UiContext";
import type { CaseReviewAgentBindings } from "../../shared/model/caseReviewAgentBindings";
import type { PageLocation } from "../../shared/model/pageLocation";
import { declarationsFor, dispatch, isFrontendTool } from "../../frontendTools/frontendToolDeclarations";
import { FRONTEND_TOOL_CODES } from "../../frontendTools/frontendToolNames";
import { refuse, type FrontendToolArgs, type FrontendToolCall, type FrontendToolResult } from "../../frontendTools/frontendTool";

export interface AgentFrontendTools {
  /** The screen as published right now; read at run time, so a run after `navigateTo` sees the new screen. */
  location: () => PageLocation | null;
  caseReview: () => CaseReviewAgentBindings | null;
  /** The declarations for the screen as it is now; sent with the run that starts next. */
  declarations: () => Tool[];
  /** Whether a call names a tool of the given run's declarations: the only calls the browser answers. */
  isDeclared: (name: string, declared: Tool[]) => boolean;
  /** Answers one call. The arguments arrive as the model's JSON text. */
  execute: (name: string, rawArgs: string, call: FrontendToolCall) => Promise<FrontendToolResult>;
}

/**
 * Binds the frontend tools to what the dock can reach: the published location, the Case Review's
 * registered executors and the router. Everything is read when a call arrives, never at render,
 * because a run outlives the render that started it.
 */
export function useAgentFrontendTools(): AgentFrontendTools {
  const location = useUiContext();
  const capabilities = useAgentCapabilities();
  const { binding } = useWhoDrugWorkspace();
  const caseReview = useCallback(() => {
    const screen = capabilities.caseReview();
    return screen ? { ...screen, whoDrug: binding?.current } : null;
  }, [capabilities, binding]);
  // The dock sits inside the router in the app; a test may render it without one, and then a
  // navigation fails loudly at the call rather than the render.
  const router = useRouter({ warn: false });
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);

  const navigate = useCallback(async (path: string): Promise<void> => {
    if (router === undefined) throw new Error("The dock is mounted outside the router; nothing can navigate.");
    await router.navigate({ to: path });
  }, [router]);

  const execute = useCallback(async (name: string, rawArgs: string, call: FrontendToolCall): Promise<FrontendToolResult> => {
    let args: FrontendToolArgs;
    try {
      const parsed: unknown = rawArgs.trim() === "" ? {} : JSON.parse(rawArgs);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new Error("arguments must be a JSON object");
      args = parsed as FrontendToolArgs;
    } catch (error: unknown) {
      return refuse(FRONTEND_TOOL_CODES.invalidValue, `The arguments of ${name} are not valid JSON: ${error instanceof Error ? error.message : String(error)}`);
    }
    return dispatch(name, args, { location: locationRef.current, caseReview: caseReview(), workQueue: capabilities.workQueue(), navigate, call });
  }, [capabilities, caseReview, navigate]);

  return {
    location: () => locationRef.current,
    caseReview,
    declarations: () => declarationsFor(locationRef.current, caseReview(), capabilities.workQueue()),
    isDeclared: isFrontendTool,
    execute,
  };
}
